import { TransactionChannel } from './transaction-channel';
import { TransactionStatus } from './transaction-status.enum';
import { TransactionType } from './transaction-type.enum';

export interface ITransaction {
  id: string;
  dispatch: 'NOW' | 'LATER';
  last4Digits?: string;
  amount: number;
  amountRefunded?: number;
  isReversed: boolean;
  isReversal: boolean;
  isPreAuth: boolean;
  remainingValue?: number;
  status: TransactionStatus;
  createdOn: Date;
  updatedOn?: Date;
  channel: TransactionChannel;
  lastError?: string;
  accountId: number;
  currencyCountry: string;
  cardHolderDetails: CardHolderDetails;
  transactionType: TransactionType;
  crossReference: string;
  maskedCard: string;
  expiryDate: string; // card number expiry
  paymentReference: string;
  paymentDetail: string;
  operatorId?: string;
  operatorName: string;
  originId: string;
  countryCode: string;
  currencyCode: string; // 826
  basket?: BasketDetails;
  billingAddress?: TransactionAddress;
  deliveryAddress?: TransactionAddress;
  transactionResult: ITransactionResult;
  transactTransactionType: string;
  merchant: Merchant;
  /** The acquirer's auth code for the original authorisation. A pre-auth completion must quote it back. */
  authorisationCode?: string;
}

// Optionality mirrors the TransactDirect basket spec:
// https://docs.monek.com/TransactDirect/key-features/basket-details/
export interface BasketDetails {
  // Inclusive (default) | Exclusive | Exempt in the spec; some responses carry it numerically.
  taxType?: number | string;
  basketItems?: BasketItem[];
  basketDiscounts?: BasketDiscount[];
  basketTaxes?: BasketTax[];
  delivery?: BasketDelivery;
  invoice?: BasketInvoice;
}

export interface BasketItem {
  description: string;
  quantity: number;
  unitPrice: number;
  unitOfMeasure?: string;
  total?: number;
  taxCode?: string;
  taxRate?: number;
  taxAmount?: number;
  sku?: string;
  commodityCode?: string;
  discountAmount?: number;
}

export interface BasketDiscount {
  code?: string;
  description: string;
  rate?: number;
  amount: number;
  taxCode?: string;
  taxRate?: number;
  taxAmount?: number;
}

export interface BasketTax {
  code: string;
  description?: string;
  rate: number;
  amount: number;
}

export interface BasketDelivery {
  carrier: string;
  amount: number;
  taxCode?: string;
  taxRate?: number;
  taxAmount?: number;
}

export interface BasketInvoice {
  invoiceNumber?: string;
  orderNumber?: string;
  // The addressee name (who the invoice is billed to).
  addressee?: string;
  // Legacy field name still returned by some responses; superseded by customerTaxNumber.
  taxRegistrationNumber?: string;
  // The addressee's tax registration number.
  customerTaxNumber?: string;
  // The tax registration number of the merchant.
  cardAcceptorTaxNumber?: string;
  isTaxExempt?: boolean;
  dueDate?: string;
  issueDate?: string;
  orderDate?: string;
}

export interface ITransactionResult {
  crossReference: string;
  responseCode: string;
  responseMessage: string
  /** The acquirer's auth code, as served by the legacy Odin.Transactions detail path. */
  authorisationCode?: string;
  terminalId: string;
  trackingToken: string;
}

// Billing or delivery address supplied with the transaction (Thor Transact.Address)
export interface TransactionAddress {
  name?: string;
  company?: string;
  line1?: string;
  line2?: string;
  line3?: string;
  city?: string;
  county?: string;
  postcode?: string;
  country?: string;
  countryCode?: string;
}

/** address lines in display order, blanks removed */
export function addressLines(address?: TransactionAddress | null): string[] {
  if (!address) return [];
  return [address.name, address.company, address.line1, address.line2, address.line3, address.city, address.county, address.postcode, address.country]
    .map((part) => (part || '').trim())
    .filter((part) => !!part);
}

export interface CardHolderDetails {
  name?: string;
  address: string;
  postcode: string;
  phoneNumber: string;
  emailAddress: string;
  iPv4Address: string;
}

export interface Merchant {
  acquirer: string;
  location: string;
  merchantNumber: string;
  monekId: string;
  name: string;
}